import React, { useState } from "react";
import {
  Activity,
  Thermometer,
  Gauge,
  Droplets,
  Battery,
  Cpu,
  Clock,
  AlertCircle,
  AlertTriangle,
  Settings,
  Zap,
} from "lucide-react";
import { getStateDescriptions } from "../../utils/things/PRECISION";
import { useEffect } from "react";
import axios from "../../utils/axiosInstance";
// --- Status helper functions ---
function getRPMStatus(rpm) {
  if (rpm === 0) return "error";
  if (rpm < 1000 || rpm > 2800) return "error";
  if (rpm > 2500) return "warning";
  return "success";
}
function getTemperatureStatus(temp) {
  if (temp === 0) return "error";
  if (temp > 110) return "error";
  if (temp > 95) return "warning";
  return "success";
}
function getPressureStatus(pressure) {
  if (pressure === 0) return "error";
  if (pressure < 40) return "warning";
  return "success";
}
function getBatteryStatus(battery) {
  if (battery === 0) return "error";
  if (battery < 11.5) return "error";
  if (battery < 12.0) return "warning";
  return "success";
}
function getEngineHours(hours) {
  if (hours === 0) return "error";
  return "success";
}

// --- Reusable Row ---
const DataRow = ({ icon, label, value }) => (
  <div className="flex items-center gap-2 whitespace-nowrap">
    {icon && <span className="flex-shrink-0">{icon}</span>}
    <span className="text-sm text-slate-400 flex-shrink-0">{label}</span>
    <span className="text-sm font-bold text-white ml-auto">{value}</span>
  </div>
);

// --- Main Component ---
export default function EngineData({ data }) {
  const { stateDescr } = getStateDescriptions(
    data?.response?.CTRL_TYPE,
    data?.response?.STATE,
    data?.response?.SHUTDOWN_ENUM,
  );

  const [mockData] = useState({
    STATE_DESCR: "N/A",
    response: {
      ENGINE_SPEED: 0,
      ENGINE_COOLANT_TEMP: 0,
      ENGINE_MANIFOLD_PRES: 0,
      ENGINE_OIL_PRES: 0,
      SYSTEM_VOTLS: 0,
      ENGINE_HOURMETER: 0,
      SPN1: 0,
      FMI1: 0,
      CM1: 0,
    },
  });

  const currentData = data || mockData;

  // Determine header color dynamically (optional logic)
  const rpm = currentData.response?.ENGINE_SPEED || 0;
  const headerBg =
    currentData.response?.STATE != 12 && currentData.response?.STATE != 130  && currentData.response?.STATE != -1
      ? "bg-green-800"
      : currentData.response?.STATE === -1
      ? "bg-orange-500"
      : "bg-red-800";

  const [flag, setFlag] = useState(-100);
  useEffect(() => {
    const fetchData = async () => {
      if (!data?.unitId) return;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/iot/device-registers/${data?.unitId}/1`
        );

        if (
          response.data.response.length > 0 && data.time <= response.data.response[0].time &&
          response.data.response[0].type === "MODBUS"
        ) {
          setFlag(-1);
        }

        if (!response)
          throw new Error(`HTTP error! status: ${response.status}`);
      } catch (err) {
        console.error("Error fetching engine data:", err);
      } finally {
      }
    };
    fetchData();
  }, [data?.unitId, data?.time]);

  return (
    <div className="flex-1">
      {/* Top colored status bar */}
      {flag === -1 ? (
        <div className="flex items-center p-3 bg-orange-500">
          <h3 className="text-sm font-semibold text-white">MODBUS ERROR</h3>
        </div>
      ) : (
        <div className={`flex items-center p-3 ${headerBg}`}>
          <h3 className="text-sm font-semibold text-white">
            {stateDescr || "Loading Engine Status ..."}
          </h3>
        </div>
      )}
      {/* Content section */}
      {currentData?.response?.CTRL_TYPE === "C5E" ||
      currentData?.response?.CTRL_TYPE === "C5EPLG" ? (
        <div className="bg-slate-800 p-3">
          {/* Section Title */}
          <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-700">
            <Settings className="w-5 h-5 text-cyan-400" />
            <h3 className="text-sm font-semibold text-white">
              Electric Motor Data
            </h3>
          </div>

          {/* Electric Motor Data Rows */}
          <div className="space-y-1.5">
            <DataRow
              icon={<Activity className="w-4 h-4 text-green-400" />}
              label="Status"
              value={stateDescr || "Running"}
            />
            <DataRow
              icon={<Gauge className="w-4 h-4 text-blue-400" />}
              label="Main Motor Speed Ref"
              value={`${currentData.response.AI10 || 0} RPM`}
            />
            <DataRow
              icon={<Zap className="w-4 h-4 text-rose-400" />}
              label="Main Motor Amps"
              value={`${currentData.response?.AI11 || 0} AMP`}
            />
            <DataRow
              icon={<Zap className="w-4 h-4 text-violet-400" />}
              label="Cooler Motor Amps"
              value={`${currentData.response?.AI12 || 0} AMP`}
            />
            <DataRow
              icon={<Battery className="w-4 h-4 text-teal-400" />}
              label="Battery"
              value={`${currentData.response?.SYSTEM_VOTLS || 0} V`}
            />
            <DataRow
              icon={<Clock className="w-4 h-4 text-grey-400" />}
              label="Motor Hourmeter"
              value={`${currentData.response?.ENGINE_HOURMETER || 0} hrs`}
            />
          </div>
        </div>
      ) : (
        <div className="bg-slate-800 p-3">
          {/* Section Title */}
          <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-700">
            <Settings className="w-5 h-5 text-orange-400" />
            <h3 className="text-sm font-semibold text-white">Engine Data</h3>
          </div>

          {/* Data Rows */}
          <div className="space-y-1.5">
            <DataRow
              icon={<Activity className="w-4 h-4 text-green-400" />}
              label="Status"
              value={stateDescr || "AT_LOAD"}
            />
            <DataRow
              icon={<Gauge className="w-4 h-4 text-blue-400" />}
              label="RPM"
              value={`${currentData.response?.ENGINE_SPEED || 0} RPM`}
            />
            <DataRow
              icon={<Thermometer className="w-4 h-4 text-rose-400" />}
              label="Coolant Temp"
              value={`${currentData.response?.ENGINE_COOLANT_TEMP || 0} °F`}
            />
            <DataRow
              icon={<Gauge className="w-4 h-4 text-cyan-400" />}
              label="Manifold Pressure"
              value={`${currentData.response?.ENGINE_MANIFOLD_PRES || 0} PSI`}
            />
            <DataRow
              icon={<Droplets className="w-4 h-4 text-sky-400" />}
              label="Oil Pressure"
              value={`${currentData.response?.ENGINE_OIL_PRES || 0} PSI`}
            />
            <DataRow
              icon={<Battery className="w-4 h-4 text-violet-400" />}
              label="Battery"
              value={`${currentData.response?.SYSTEM_VOTLS || 0} V`}
            />
            <DataRow
              icon={<Clock className="w-4 h-4 text-slate-400" />}
              label="Engine Hours"
              value={`${currentData.response?.ENGINE_HOURMETER || 0} hrs`}
            />
            <DataRow
              icon={<AlertCircle className="w-4 h-4 text-yellow-400" />}
              label="SPN1"
              value={currentData.response?.SPN1 || 0}
            />
            <DataRow
              icon={<AlertTriangle className="w-4 h-4 text-orange-400" />}
              label="FMI1"
              value={currentData.response?.FMI1 || 0}
            />
            <DataRow
              icon={<Cpu className="w-4 h-4 text-teal-400" />}
              label="CM1"
              value={currentData.response?.CM1 || 0}
            />
          </div>
        </div>
      )}
    </div>
  );
}
